import * as THREE from 'three';
import { getHersheyFont } from './hershey-font.js';

// A hand writing the track's name in a corner of the frame, as one ribbon of
// geometry with a progressive reveal.
//
// This was manuscript.js, a visualizer in its own right - a whole page given
// over to the title in a nib hand. It is now a COMPONENT: sumi.js signs its
// wash with it, bottom right, small. The writing was the only part of that
// visualizer worth keeping; its paper and its ink blots were a second, weaker
// copy of what sumi.js already does better, which is exactly why merging the
// two was the right call rather than keeping both.
//
// THE WHOLE PIECE IS THE STROKE. Progressive handwriting cannot be done with
// an ordinary font: a normal typeface gives closed OUTLINES, so revealing one
// progressively wipes a filled shape and reads as a mask sliding over a word.
// A Hershey single-stroke face gives centreline polylines, which is a pen's
// actual trajectory - see tools/build-hershey.mjs for the conversion and the
// font's licence, and tools/test-hershey.mjs for the off-by-one in the glyph
// table that this file's output makes immediately visible when it regresses.

// --- layout -----------------------------------------------------------------
// Height units, the same convention the vertex shader below converts to NDC:
// y in [-0.5, 0.5], x in [-aspect/2, aspect/2]. NOTE this is HALF the scale
// safeZone.glsl.js uses (that one is in ndc, y in [-1, 1]) - the safe-zone
// numbers quoted in the comments here are already divided by two.
const TILT_DEG = -3;
// The block hangs from its BOTTOM-RIGHT corner: right-aligned against a margin
// that mirrors the left margin the old full-page layout used, and stacked
// upward from the last baseline. Anchoring the bottom is what keeps a
// two-line title growing UP into empty frame instead of down off the edge.
const RIGHT_MARGIN = 0.44;      // fraction of frame width from centre
const BOTTOM_BASELINE_Y = -0.40;
const MAX_TEXT_WIDTH = 0.26;    // fraction of frame width the block may occupy
const SIZE_MAX = 0.048;         // cap height, in height units
const SIZE_MIN = 0.026;
const ARTIST_SIZE_RATIO = 0.62;
const LINE_GAP = 1.5;           // multiples of cap height
// Wider than the 1.6 the full-page layout used. There the artist was INDENTED
// to the right of the title's left edge, so the two lines rarely met; here both
// are right-aligned flush, which parks the artist directly under the title's
// descenders AND under the low end of the tilt. Measured on "Everything In Its
// Right Place" / "Radiohead", 1.6 left them touching.
const ARTIST_GAP = 2.1;
const MAX_TITLE_CHARS_PER_LINE = 22;
const ARTIST_MAX_CHARS = 24;

// --- the stroke -------------------------------------------------------------
const WRITE_DELAY_S = 1.2;
const WRITE_SECONDS_MIN = 4;
const WRITE_SECONDS_MAX = 9;
const CHARS_FOR_MAX_DURATION = 46;
// Nib width was measured against a 0.115 cap height, back when the writing
// owned the whole page. It is a PHYSICAL width in pixels, so it does not
// follow the cap height on its own - shrinking the text without shrinking the
// nib gives a fat marker scrawl, not a small hand. Scaled by the size ratio,
// with a floor: below about a pixel and a half a stroke stops being a line and
// starts being a dotted suggestion.
const NIB_REFERENCE_SIZE = 0.115;
const NIB_PX_MIN = 4;
const NIB_PX_MAX = 9;
const NIB_PX_FLOOR = 1.5;
// How sharply a corner slows the hand, from the turn between adjacent
// segments. A hand decelerates into a curve and runs along a straight, and a
// constant-speed trace is the single thing that most obviously gives away a
// machine.
const CORNER_SLOWDOWN = 3.2;
const SPEED_LUT_SIZE = 256;
// Freshly laid ink: darker and a little wider, then it dries.
const WET_ARC = 0.03;
const WET_DARKEN = 0.28;
const WET_WIDEN = 0.22;
// A nib puts down more ink where it lands and where it lifts.
const ENTRY_PRESSURE = 0.35;
const ENTRY_FRACTION = 0.18;
const BAND_WIDTH_MOD = 0.08;

const TRACK_FADE_S = 2.5;
const INK_BASE = 0x14100e;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// Lays the glyph polylines of one string end to end. Pen-up gaps between
// strokes are NOT filled in: they stay as breaks, which is what stops the trace
// drawing a ghost line from one letter to the next.
function layoutString(font, text, size, originX, originY, tiltRad, out) {
  const cos = Math.cos(tiltRad), sin = Math.sin(tiltRad);
  let cursor = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const glyph = font.glyphs[ch];
    const advance = font.advances[ch] != null ? font.advances[ch] : 0.6;
    if (glyph) {
      for (let s = 0; s < glyph.length; s++) {
        const src = glyph[s];
        const stroke = [];
        for (let k = 0; k < src.length; k++) {
          const gx = (src[k][0] + cursor) * size;
          const gy = src[k][1] * size;
          stroke.push([
            originX + gx * cos - gy * sin,
            originY + gx * sin + gy * cos
          ]);
        }
        if (stroke.length > 1) out.push(stroke);
      }
    }
    cursor += advance;
  }
  return cursor * size;
}

function measureString(font, text, size) {
  let cursor = 0;
  for (let i = 0; i < text.length; i++) {
    const a = font.advances[text[i]];
    cursor += a != null ? a : 0.6;
  }
  return cursor * size;
}

// A long title is made SMALLER and wrapped, never truncated. The first version
// filled lines up to a character budget and stopped after two, which silently
// DROPPED every remaining word - measured on "Everything In Its Right Place
// (Remastered Extended Club Version)", the tail simply vanished. Splitting at
// the most balanced word boundary keeps every word by construction: one line if
// it is short, otherwise two, and the size solve below shrinks until whatever
// came out actually fits.
function wrapTitle(text) {
  const trimmed = text.trim();
  if (trimmed.length <= MAX_TITLE_CHARS_PER_LINE) return [trimmed];
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < 2) return [trimmed];
  let bestIndex = 1;
  let bestScore = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = words.slice(0, i).join(' ').length;
    const b = words.slice(i).join(' ').length;
    const score = Math.abs(a - b);
    if (score < bestScore) { bestScore = score; bestIndex = i; }
  }
  return [words.slice(0, bestIndex).join(' '), words.slice(bestIndex).join(' ')];
}

// A track can credit a whole roster. "Cumbiasound, Lis Flores Varela, Boogie
// Castillo" is 46 characters, and the size solve cannot rescue it: it bottoms
// out at SIZE_MIN with the line still past its width budget. Shrinking further
// is not the answer either; past a point the hand stops reading as a hand.
//
// So a long roster is CREDITED rather than written out, the way a sleeve does
// it: the first name, then how many others. Truncation with an ellipsis would
// say the name was cut off; "& 2" says there are two more people.
function shortenArtists(artist) {
  const trimmed = (artist || '').trim();
  if (trimmed.length <= ARTIST_MAX_CHARS) return trimmed;
  const names = trimmed.split(',').map((n) => n.trim()).filter(Boolean);
  // One long name is one long name. Breaking inside it would rename the
  // artist, so it is left whole and the size solve takes it from there.
  if (names.length < 2) return trimmed;
  return names[0] + ' & ' + (names.length - 1);
}

// Screen-space, deliberately: the writing is a signature ON the frame, not an
// object in the scene, so it ignores the camera entirely and cannot drift with
// sumi.js's Ken Burns.
const vertexShader = `
  precision mediump float;

  attribute float iArc;
  attribute float iWidth;   // magnitude = half-width in px, sign = which edge
  attribute vec2 iNormal;

  uniform float uAspect;
  uniform float uPixelHeight;
  uniform float uWidthScale;
  uniform float uHead;

  varying float vArc;
  varying float vSide;

  void main() {
    vArc = iArc;
    vSide = iWidth >= 0.0 ? 1.0 : -1.0;

    // Wet ink is slightly wider, and the whole nib breathes a few percent with
    // the mid band.
    float wet = 1.0 - smoothstep(0.0, ${WET_ARC.toFixed(3)}, max(uHead - iArc, 0.0));
    float halfPx = abs(iWidth) * uWidthScale * (1.0 + ${WET_WIDEN.toFixed(3)} * wet);
    // Pixels -> height units. x is divided by the aspect on the way to NDC
    // below, so the offset is applied in height units on both axes and the
    // stroke keeps a constant width whatever the frame shape.
    float halfH = halfPx / max(uPixelHeight, 1.0);
    vec2 pos = position.xy + iNormal * halfH * vSide;

    gl_Position = vec4(pos.x / (uAspect * 0.5), pos.y * 2.0, 0.0, 1.0);
  }
`;

const fragmentShader = `
  precision mediump float;

  uniform float uHead;
  uniform vec3 uInk;
  uniform float uOpacity;
  uniform float uEdgeNoise;

  varying float vArc;
  varying float vSide;

  float hash11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }
  float noise11(float p) {
    float i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(hash11(i), hash11(i + 1.0), f);
  }

  void main() {
    // Not yet written. This is the reveal, and it is a per-fragment test on the
    // vertex's own arc position rather than a mask sliding over the word.
    if (vArc > uHead) discard;

    // Soft, irregular edge - the fibre drinks the ink unevenly, so the boundary
    // is never the clean line the geometry actually is. vSide runs -1..1 across
    // the ribbon, so |vSide| is distance from the centreline.
    float irregular = uEdgeNoise > 0.5 ? 0.80 + 0.40 * noise11(vArc * 380.0) : 1.0;
    // Full opacity across most of the nib, feathering only the outer quarter.
    // The first version faded from 35% of the half-width outward, which on a
    // 3px stroke left barely one pixel at full strength - measured, the darkest
    // pixel in the whole frame was 0.534 against paper at 0.74, so the writing
    // was a grey suggestion rather than ink. A nib lays down a solid line with
    // a soaked edge, not a gradient.
    float across = 1.0 - smoothstep(0.72 * irregular, 1.06 * irregular, abs(vSide));

    // Wet: the last stretch laid down is darker, then it dries back.
    float wet = 1.0 - smoothstep(0.0, ${WET_ARC.toFixed(3)}, max(uHead - vArc, 0.0));
    float ink = across * (1.0 + ${WET_DARKEN.toFixed(3)} * wet);

    gl_FragColor = vec4(uInk, clamp(ink, 0.0, 1.0) * uOpacity);
  }
`;

export class Handwriting {
  // edgeNoise follows the host's own quality tier - the same trade-off
  // sumi.js's TIER_LAYOUT.contourNoise already makes for its ink field.
  constructor(options) {
    const opts = options || {};
    this._edgeNoise = opts.edgeNoise !== false;
    this._renderOrder = opts.renderOrder != null ? opts.renderOrder : 10;
    this._forcedText = urlParam('text');
    this._forcedSpeed = parseFloat(urlParam('writespeed'));
    this._bounds = null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    this._uniforms = {
      uAspect: { value: camera.aspect },
      uPixelHeight: { value: 1080 },
      uWidthScale: { value: 1 },
      uHead: { value: 0 },
      uInk: { value: new THREE.Color(INK_BASE) },
      uOpacity: { value: 1 },
      uEdgeNoise: { value: this._edgeNoise ? 1 : 0 }
    };
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      // The host owns the depth buffer; the signature sits on top of whatever
      // it painted and takes no part in it.
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Same reason sumi.js gives at length: ACES would lift this file's
      // carefully-placed ink luminance somewhere unpredictable.
      toneMapped: false
    });

    this._mesh = null;
    this._geometry = null;
    this._speedLut = null;
    this._time = 0;
    this._trackKey = null;
    this._writeStart = null;
    this._writeDuration = WRITE_SECONDS_MIN;
    this._fade = 1;
    this._fadingOut = false;
    this._pendingProfile = null;
  }

  // The block's extent in height units, or null before anything is written.
  // sumi.js reads this to keep its ink deposits off the words.
  get bounds() {
    return this._bounds;
  }

  setInkColor(color) {
    this._uniforms.uInk.value.copy(color);
  }

  // Title, then artist, then album, then nothing at all. A placeholder is never
  // written: an unsigned wash is a legitimate wash, and "Unknown Track" in
  // someone's handwriting is not.
  _chooseText(profile) {
    if (this._forcedText != null) return { title: this._forcedText, artist: '' };
    if (profile.title) {
      return { title: profile.title, artist: shortenArtists(profile.artist) };
    }
    if (profile.artist) return { title: profile.artist, artist: '' };
    if (profile.album) return { title: profile.album, artist: '' };
    return null;
  }

  _build(profile) {
    this._disposeMesh();
    this._bounds = null;
    const font = getHersheyFont();
    const text = this._chooseText(profile);
    if (!font || !text || !text.title) return;

    const tilt = THREE.MathUtils.degToRad(TILT_DEG);
    const lines = wrapTitle(text.title);
    const aspect = this._camera.aspect;

    // Size is SOLVED, not guessed: shrink until the widest line fits the
    // block's width budget. A long title gets smaller; it is never clipped and
    // never runs off the frame.
    let size = SIZE_MAX;
    const budget = MAX_TEXT_WIDTH * aspect;
    for (let guard = 0; guard < 40; guard++) {
      let widest = 0;
      for (let i = 0; i < lines.length; i++) {
        widest = Math.max(widest, measureString(font, lines[i], size));
      }
      // The ARTIST counts too - leaving it out of the solve is what let a long
      // artist name run off the right edge while the title sat comfortably
      // inside the budget.
      if (text.artist) {
        widest = Math.max(widest,
          measureString(font, text.artist, size * ARTIST_SIZE_RATIO));
      }
      if (widest <= budget) break;
      if (size <= SIZE_MIN) break;
      size *= 0.93;
    }
    size = Math.max(SIZE_MIN, size);

    // Right-aligned, stacked upward from the bottom baseline. Every line gets
    // its own origin, since each is a different width and the tilt is applied
    // about that origin.
    const rightX = RIGHT_MARGIN * aspect;
    const artistSize = size * ARTIST_SIZE_RATIO;
    const strokes = [];
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    const place = (str, lineSize, baselineY) => {
      const w = measureString(font, str, lineSize);
      const originX = rightX - w;
      layoutString(font, str, lineSize, originX, baselineY, tilt, strokes);
      if (originX < x0) x0 = originX;
      if (rightX > x1) x1 = rightX;
      // The tilt drops the right-hand end of every line, so the block's real
      // bottom is below the baseline by the run times the tangent, on top of
      // the descenders.
      const drop = w * Math.tan(-tilt);
      if (baselineY - lineSize * 0.35 - drop < y0) y0 = baselineY - lineSize * 0.35 - drop;
      if (baselineY + lineSize > y1) y1 = baselineY + lineSize;
    };

    let baseline = BOTTOM_BASELINE_Y;
    if (text.artist) {
      place(text.artist, artistSize, baseline);
      baseline += artistSize * ARTIST_GAP;
    }
    // Bottom-up, so the LAST title line sits directly above the artist.
    for (let i = lines.length - 1; i >= 0; i--) {
      place(lines[i], size, baseline);
      baseline += size * LINE_GAP;
    }
    if (!strokes.length) return;

    this._bounds = { x0: x0, x1: x1, y0: y0, y1: y1 };
    this._makeGeometry(strokes, size,
      text.title.length + (text.artist ? text.artist.length : 0));
  }

  // ONE ribbon, ONE draw call. Pen-up gaps are simply ABSENT from the geometry
  // rather than drawn transparent: nothing exists between two unconnected
  // strokes, so no ghost line can appear even if a uniform is wrong later.
  _makeGeometry(strokes, size, charCount) {
    const nibScale = Math.max(
      NIB_PX_FLOOR / NIB_PX_MIN, size / NIB_REFERENCE_SIZE);

    // Arc length of the whole concatenated path INCLUDING the gaps, so the hand
    // spends time travelling between letters exactly as a real one would.
    let total = 0;
    const startArc = [];
    let cx = null, cy = null;
    for (let s = 0; s < strokes.length; s++) {
      const pts = strokes[s];
      if (cx !== null) total += Math.hypot(pts[0][0] - cx, pts[0][1] - cy);
      startArc.push(total);
      for (let i = 1; i < pts.length; i++) {
        total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
      }
      cx = pts[pts.length - 1][0];
      cy = pts[pts.length - 1][1];
    }
    if (total <= 0) return;

    const positions = [];
    const normals = [];
    const arcs = [];
    const widths = [];
    const costArc = [];
    const costVal = [];

    for (let s = 0; s < strokes.length; s++) {
      const pts = strokes[s];
      const n = pts.length;

      const pointArc = [startArc[s]];
      for (let i = 1; i < n; i++) {
        pointArc.push(pointArc[i - 1]
          + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
      }
      const strokeLen = (pointArc[n - 1] - pointArc[0]) || 1;

      const norm = [];
      const halfPx = [];
      for (let i = 0; i < n; i++) {
        // Averaged tangent, so adjacent segments share an offset direction and
        // the joins stay continuous.
        const prev = pts[Math.max(0, i - 1)];
        const next = pts[Math.min(n - 1, i + 1)];
        let dx = next[0] - prev[0], dy = next[1] - prev[1];
        const len = Math.hypot(dx, dy) || 1;
        dx /= len; dy /= len;
        norm.push([-dy, dx]);

        let turn = 0;
        if (i > 0 && i < n - 1) {
          let ax = pts[i][0] - pts[i - 1][0], ay = pts[i][1] - pts[i - 1][1];
          let bx = pts[i + 1][0] - pts[i][0], by = pts[i + 1][1] - pts[i][1];
          const la = Math.hypot(ax, ay) || 1, lb = Math.hypot(bx, by) || 1;
          ax /= la; ay /= la; bx /= lb; by /= lb;
          turn = Math.acos(Math.max(-1, Math.min(1, ax * bx + ay * by)));
        }
        const sharp = Math.min(1, turn / Math.PI);   // 0 straight, 1 hairpin

        const along = (pointArc[i] - pointArc[0]) / strokeLen;
        const ends = Math.max(1 - along / ENTRY_FRACTION,
                              1 - (1 - along) / ENTRY_FRACTION);
        const pressure = 1 + ENTRY_PRESSURE * Math.max(0, ends);
        // Wide where slow, narrow where fast: the nib deposits more ink when
        // the hand lingers.
        halfPx.push((NIB_PX_MIN + (NIB_PX_MAX - NIB_PX_MIN) * sharp)
          * pressure * nibScale * 0.5);

        costArc.push(pointArc[i] / total);
        costVal.push(1 + CORNER_SLOWDOWN * sharp);
      }

      for (let i = 0; i < n - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        const na = norm[i], nb = norm[i + 1];
        const aa = pointArc[i] / total, ab = pointArc[i + 1] / total;
        const wa = halfPx[i], wb = halfPx[i + 1];
        // Two triangles: (a+, b+, a-) and (b+, b-, a-)
        const verts = [
          [a, na, aa, wa], [b, nb, ab, wb], [a, na, aa, -wa],
          [b, nb, ab, wb], [b, nb, ab, -wb], [a, na, aa, -wa]
        ];
        for (let v = 0; v < verts.length; v++) {
          const q = verts[v];
          positions.push(q[0][0], q[0][1], 0);
          normals.push(q[1][0], q[1][1]);
          arcs.push(q[2]);
          widths.push(q[3]);
        }
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('iNormal', new THREE.Float32BufferAttribute(normals, 2));
    geometry.setAttribute('iArc', new THREE.Float32BufferAttribute(arcs, 1));
    geometry.setAttribute('iWidth', new THREE.Float32BufferAttribute(widths, 1));
    this._geometry = geometry;
    this._mesh = new THREE.Mesh(geometry, this._material);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = this._renderOrder;
    this._scene.add(this._mesh);

    this._buildSpeedLut(costArc, costVal);
    this._writeDuration = this._forcedSpeed > 0
      ? this._forcedSpeed
      : WRITE_SECONDS_MIN + (WRITE_SECONDS_MAX - WRITE_SECONDS_MIN)
        * Math.min(1, charCount / CHARS_FOR_MAX_DURATION);
  }

  // Maps normalised TIME to normalised ARC, by integrating the per-point
  // slowness along the path. This is what makes the head crawl through curves
  // and run along straights - a linear head is a plotter, not a hand.
  _buildSpeedLut(costArc, costVal) {
    const n = costArc.length;
    const lut = new Float32Array(SPEED_LUT_SIZE);
    if (n < 2) {
      for (let i = 0; i < SPEED_LUT_SIZE; i++) lut[i] = i / (SPEED_LUT_SIZE - 1);
      this._speedLut = lut;
      return;
    }
    const cum = new Float32Array(n);
    let acc = 0;
    for (let i = 1; i < n; i++) {
      const dArc = Math.max(0, costArc[i] - costArc[i - 1]);
      acc += dArc * (costVal[i] + costVal[i - 1]) * 0.5;
      cum[i] = acc;
    }
    if (acc <= 0) {
      for (let i = 0; i < SPEED_LUT_SIZE; i++) lut[i] = i / (SPEED_LUT_SIZE - 1);
    } else {
      let j = 0;
      for (let i = 0; i < SPEED_LUT_SIZE; i++) {
        const target = (i / (SPEED_LUT_SIZE - 1)) * acc;
        while (j < n - 1 && cum[j] < target) j++;
        lut[i] = costArc[j];
      }
      lut[SPEED_LUT_SIZE - 1] = 1;
    }
    this._speedLut = lut;
  }

  _arcAtTime(t) {
    if (!this._speedLut) return t;
    const f = Math.max(0, Math.min(1, t)) * (SPEED_LUT_SIZE - 1);
    const i = Math.floor(f);
    const frac = f - i;
    const a = this._speedLut[i];
    const b = this._speedLut[Math.min(SPEED_LUT_SIZE - 1, i + 1)];
    return a + (b - a) * frac;
  }

  _disposeMesh() {
    if (this._mesh) { this._scene.remove(this._mesh); this._mesh = null; }
    if (this._geometry) { this._geometry.dispose(); this._geometry = null; }
    this._speedLut = null;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // Pause freezes the nib in place: _time drives the head and the fade
    // alike, so not advancing it stops both at once.
    if (!paused) this._time += deltaTime;

    // Track change: the ink fades over TRACK_FADE_S, THEN the new name is
    // written. Two phases, never overlapping, so the corner is never half one
    // title and half the next.
    const key = (profile.trackId || '') + '|' + (profile.title || '') + '|' + (profile.artist || '');
    if (key !== this._trackKey) {
      if (this._trackKey === null) {
        this._trackKey = key;
        this._build(profile);
        this._writeStart = this._time + WRITE_DELAY_S;
      } else {
        this._trackKey = key;
        this._pendingProfile = profile;
        this._fadingOut = true;
      }
    }
    if (this._fadingOut && !paused) {
      this._fade -= deltaTime / TRACK_FADE_S;
      if (this._fade <= 0) {
        this._fade = 1;
        this._fadingOut = false;
        this._build(this._pendingProfile || profile);
        this._pendingProfile = null;
        this._writeStart = this._time + WRITE_DELAY_S;
      }
    }

    // The head. Once it reaches 1 it STAYS at 1 - the name is written, and a
    // written name does not blink, drift or redraw itself.
    let head = 0;
    if (this._writeStart !== null) {
      const t = (this._time - this._writeStart) / this._writeDuration;
      head = t <= 0 ? 0 : (t >= 1 ? 1 : this._arcAtTime(t));
    }
    this._uniforms.uHead.value = head;
    this._uniforms.uOpacity.value = this._fadingOut ? Math.max(0, this._fade) : 1;
    this._uniforms.uAspect.value = this._camera.aspect;
    this._uniforms.uPixelHeight.value =
      (typeof window !== 'undefined')
        ? window.innerHeight * Math.min(window.devicePixelRatio || 1, 2)
        : 1080;

    const director = profile.director;
    const bands = (director && director.bands) || null;
    const live = !!(director && director.hasReliableGrid && bands);
    // The only reactivity the writing itself has, and deliberately so.
    const mid = live ? bands.mid : 0.5;
    this._uniforms.uWidthScale.value = 1 + (mid - 0.5) * 2 * BAND_WIDTH_MOD;
  }

  dispose() {
    this._disposeMesh();
    this._material.dispose();
  }
}
